import { useEffect, useState, type ReactNode } from 'react';
import { CookieIcon, MonitorSmartphone, ServerOff } from 'lucide-react';
import { isBackendConfigured } from '@/api/miniapp/client';
import { useAuthStore } from '@/store/authStore';
import { useSessionHeartbeat } from '@/app/hooks/useSessionHeartbeat';
import { BootScreen } from './BootScreen';
import { Button } from '@/components/ui/Button';
import { StateView } from '@/components/ui/Feedback';
import { RegistrationFlow } from '@/features/registration/RegistrationFlow';
import { describeLaunchData } from './launchInfo';

/**
 * Ворота сессии: до успешного входа основное приложение не рендерится.
 *
 * Когда адрес бэкенда не задан (`VITE_AUTH_BASE_URL` пуст), гейт прозрачен:
 * входить некуда. Данных в таком приложении нет, экраны покажут свои ошибки —
 * это конфигурация сборки, а не рабочий режим.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  // Диагностика запуска нужна именно здесь: раздел наладки доступен только
  // после входа, а разбираться приходится как раз тогда, когда входа нет
  const [launch, setLaunch] = useState<string | null>(null);
  const phase = useAuthStore((state) => state.phase);
  const step = useAuthStore((state) => state.step);
  const error = useAuthStore((state) => state.error);
  const replaced = useAuthStore((state) => state.replacedByOtherDevice);
  const everAuthenticated = useAuthStore((state) => state.everAuthenticated);
  const cookieBlocked = useAuthStore((state) => state.cookieBlocked);
  const retryAfterCookieBlock = useAuthStore((state) => state.retryAfterCookieBlock);
  const authenticate = useAuthStore((state) => state.authenticate);
  const continueHere = useAuthStore((state) => state.continueHere);

  useSessionHeartbeat();

  useEffect(() => {
    // При входе с другого устройства автоматический повторный вход запрещён:
    // иначе два устройства бесконечно перехватывали бы сессию друг у друга
    if (isBackendConfigured && phase === 'idle' && !replaced) void authenticate();
  }, [phase, replaced, authenticate]);

  if (!isBackendConfigured || phase === 'disabled') return <>{children}</>;

  if (replaced) {
    return (
      <div
        className="mx-auto flex w-full max-w-[520px] flex-col justify-center px-5"
        style={{ minHeight: 'var(--bd-viewport-height)' }}
      >
        <StateView
          icon={<MonitorSmartphone className="h-6 w-6" aria-hidden />}
          title="Вы вошли с другого устройства"
          description="Курс открыт в другом приложении. Продолжить можно только на одном устройстве."
          primary={
            <Button variant="primary" block onClick={() => void continueHere()}>
              Продолжить здесь
            </Button>
          }
        />
      </div>
    );
  }

  // Клиент не сохраняет сессию: молчаливые повторные входы бесполезны, а любой
  // экран с данными покажет ошибку. Честнее объяснить и предложить телефон
  if (cookieBlocked) {
    return (
      <div
        className="mx-auto flex w-full max-w-[520px] flex-col justify-center px-5"
        style={{ minHeight: 'var(--bd-viewport-height)' }}
      >
        <StateView
          icon={<CookieIcon className="h-6 w-6" aria-hidden />}
          title="Вход не сохраняется"
          description="Этот клиент не сохраняет данные входа, поэтому сессия теряется сразу после подключения. Откройте приложение на телефоне — там вход работает."
          primary={
            <Button variant="primary" block onClick={() => void retryAfterCookieBlock()}>
              Попробовать снова
            </Button>
          }
          secondary={
            <Button variant="ghost" block onClick={() => setLaunch(describeLaunchData())}>
              Показать данные запуска
            </Button>
          }
        >
          {launch ? (
            <pre className="select-text overflow-auto rounded-control border border-line bg-surface-2 p-2 text-left text-[10px] leading-[1.35]">
              {launch}
            </pre>
          ) : null}
        </StateView>
      </div>
    );
  }

  // Повторный вход посреди регистрации не должен разбирать экран: иначе
  // продление сессии раз в 25 секунд сбрасывало бы наполовину заполненную форму
  if ((phase === 'idle' || phase === 'authenticating') && step !== null) {
    return <RegistrationFlow />;
  }

  // Приложение уже открывалось: повторный вход идёт молча. Иначе при каждой
  // неудачной проверке сессии экран мигал бы загрузкой поверх работы —
  // так выглядит десктоп, где cookie сессии не доходит до сервера
  if ((phase === 'idle' || phase === 'authenticating') && everAuthenticated) {
    return <>{children}</>;
  }

  if (phase === 'idle' || phase === 'authenticating') return <BootScreen />;

  if (phase === 'error') {
    // Сводку запуска показываем сразу, когда дело именно в ней: иначе человек
    // присылает снимок экрана без единственных данных, по которым видно причину
    const launchIssue = Boolean(error?.includes('данных запуска'));

    return (
      <div
        className="mx-auto flex w-full max-w-[520px] flex-col justify-center px-5"
        style={{ minHeight: 'var(--bd-viewport-height)' }}
      >
        <StateView
          icon={<ServerOff className="h-6 w-6" aria-hidden />}
          title="Не удалось войти"
          description={error ?? 'Сервер не ответил. Проверьте подключение и попробуйте ещё раз.'}
          primary={
            <Button variant="primary" block onClick={() => void authenticate()}>
              Попробовать снова
            </Button>
          }
          secondary={
            <Button variant="ghost" block onClick={() => setLaunch(describeLaunchData())}>
              Показать данные запуска
            </Button>
          }
        >
          {launch ?? (launchIssue ? describeLaunchData() : null) ? (
            <pre className="select-text overflow-auto rounded-control border border-line bg-surface-2 p-2 text-left text-[10px] leading-[1.35]">
              {launch ?? describeLaunchData()}
            </pre>
          ) : null}
        </StateView>
      </div>
    );
  }

  // Регистрация проходит в отдельном потоке; основной интерфейс до неё не показывается
  if (phase === 'registration') return <RegistrationFlow />;

  return <>{children}</>;
}
